import styled from '@emotion/styled';
import {
  palette,
  spacing,
  Modal,
  Button,
  fontStyle,
  breakpoints,
} from '@playdapp/ui';
import { useAppSelector } from 'store/hooks';
import { walletName } from 'data/word';
import { convertAddress } from 'lib/util';

import type { WalletProvider } from 'types/wallet';

import Image from '@/components/Image';

type Props = {
  handleContinue: () => void;
  isOpen: boolean;
  handleOpen: (isOpen: boolean) => void;
};

const MainDescription = styled.div`
  text-align: center;
  ${fontStyle('h4')}

  ${breakpoints.down('md')} {
    ${fontStyle('h5')}
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  width: 100%;

  button {
    padding: 0.5rem 1.25rem;
    ${fontStyle('p3')}

    ${breakpoints.down('md')} {
      width: 100%;
    }
  }
`;

const SubDescription = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  ${fontStyle('p3')}
  color: ${palette.gray900};

  ${breakpoints.down('sm')} {
    ${fontStyle('p4')}
  }
`;

const DescriptionBox = styled.div`
  margin-bottom: 0.5rem;
  width: 100%;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 2rem;
  margin: 1rem 0 0rem;
  gap: 0.25rem;
  width: 100%;
  background-color: ${palette.gray200};
  border-radius: 16px;
`;

const WalletBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WalletTitle = styled.p`
  margin-bottom: ${spacing.xs};
  ${fontStyle('p5')}
  color: ${palette.gray900};
`;

const WalletImageBox = styled.figure`
  display: flex;
  align-items: center;
  gap: ${spacing.xxs};
  margin-bottom: 14px;

  p {
    color: ${palette.dgray300};
    font-size: 14px;
    font-weight: 600;
  }
`;

const WalletAddress = styled.div`
  min-width: 190px;
  padding: ${spacing.xxs} ${spacing.m};
  background-color: ${palette.white};
  border-radius: 24px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
`;

const MembersSignInModal = ({ isOpen, handleOpen, handleContinue }: Props) => {
  const { provider, account } = useAppSelector(({ wallet }) => wallet);

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      width={{ xl: '498px', lg: '498px', md: '498px' }}
      handleOpen={handleOpen}
    >
      <MainDescription>
        Sign In to your PlayDapp Members account
      </MainDescription>
      <DescriptionBox>
        <Box>
          <WalletBlock>
            <WalletTitle>Connected with</WalletTitle>
            <WalletImageBox>
              <Image
                alt=""
                src={`/${provider}.png`}
                prefix="local"
                width={24}
                height={24}
              />
              <p>{walletName[provider as WalletProvider]}</p>
            </WalletImageBox>
            <WalletAddress>
              {convertAddress((account as string) ?? '-')}
            </WalletAddress>
          </WalletBlock>
        </Box>
        <SubDescription>
          If you Continue, Your Sign In is complete.
        </SubDescription>
      </DescriptionBox>
      <ButtonContainer>
        <Button
          variant="outline"
          color="secondary"
          onClick={() => handleOpen(false)}
        >
          Cancel
        </Button>
        <Button onClick={handleContinue}>Continue</Button>
      </ButtonContainer>
    </Modal>
  );
};

export default MembersSignInModal;
